import { Injectable } from '@angular/core';

import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { PrettyUrlsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ItemType } from '@frontend/sports/common/core/data-access/sport-model';
import { RacingUrlParam } from '@frontend/sports/racing/feature/models';
import produce, { original } from 'immer';

import { TreeItem } from '../../common/item/item.model';
import { BaseTreeItem } from '../../competition-list/competition-list.models';
import { MeetingTreeItem } from '../components/greyhounds/greyhounds-meeting-switcher/meetingTreeItem';
import { DayOfRaceViewModel, RegionViewModel } from '../components/tournaments/day-of-race-lobby.component';

@Injectable({
    providedIn: 'root',
})
export class GreyhoundMeetingSwitcherService {
    state?: string | number;

    constructor(
        private sitecore: Sitecore,
        private urlConfig: PrettyUrlsConfig,
    ) {}

    toggleExpanded(tree: TreeItem, item: TreeItem): TreeItem {
        return produce(tree, (draft) => {
            this.traverse(original(draft)!, draft, (originalItem, draftItem) => {
                if (originalItem === item) {
                    draftItem.expanded = !originalItem.expanded;

                    return true;
                }

                return false;
            });
        });
    }

    disableClick(tree: TreeItem): TreeItem {
        return produce(tree, (draft) => {
            this.traverse(original(draft)!, draft, (originalItem, draftItem) => {
                draftItem.active = false;
                draftItem.children.filter((o) => o.active).forEach((child) => this.disableClick(child));

                return false;
            });
        });
    }

    enableClick(tree: TreeItem, id: number): TreeItem {
        const itemId = Number(id);
        const copy = this.disableClick(tree);

        return produce(copy, (draft) => {
            this.traverse(original(draft)!, draft, (originalItem, draftItem) => {
                if (originalItem.id === itemId) {
                    draftItem.active = true;

                    return true;
                }

                return false;
            });
        });
    }

    private traverse(root: TreeItem, draft: TreeItem, action: (originalItem: TreeItem, draftItem: TreeItem) => boolean): boolean {
        if (action(root, draft)) {
            return true;
        }

        for (const child of root.children) {
            const draftChild = draft.children[root.children.indexOf(child)];
            if (this.traverse(child, draftChild, action)) {
                return true;
            }
        }

        return false;
    }

    private generateItemTreeChildren(result: DayOfRaceViewModel): BaseTreeItem[] {
        const createBaseTreeItem = (title: string, sportId: number, type: ItemType, href: string, count: number, regionId: number[]) => {
            const item = new BaseTreeItem();
            item.title = title;
            item.id = sportId;
            item.href = href;
            item.children = [];
            item.count = count;
            item.active = ItemType.Region === type;
            item.expanded = ItemType.Region === type;
            item.regions = regionId;

            return item;
        };
        const regionsList: BaseTreeItem[] = result.regions.map((region: RegionViewModel): BaseTreeItem => {
            const childrens = region.meetings.map((meeting) =>
                createBaseTreeItem(meeting.meetingName, meeting.meetingId, ItemType.Meeting, meeting.link, meeting.fixtures.length, region.id),
            );
            const item = new MeetingTreeItem(region.name, region.id, ItemType.Region, childrens, true);

            return item;
        });

        return regionsList;
    }

    getRegionName(tree: TreeItem, item: TreeItem): string | undefined {
        const regionName = tree.children.find((o) => o.regions === item?.regions)?.title;

        return regionName ?? tree.children.find((o) => item?.regions && o.id === item.regions[0])?.title;
    }

    setByRouteParam(id: number, tree: TreeItem): TreeItem {
        return id > 0 ? this.enableClick(tree, id) : this.disableClick(tree);
    }

    private getDayofRace(day: DayOfRaceDate): string {
        return day === DayOfRaceDate.Today ? this.sitecore.horseRacing.navigation.Today : this.sitecore.horseRacing.navigation.Tomorrow;
    }

    buildMeetingTree(model: DayOfRaceViewModel, day: DayOfRaceDate): MeetingTreeItem {
        const children = this.generateItemTreeChildren(model);
        const dayofrace = this.getDayofRace(day);

        return new MeetingTreeItem(dayofrace, [SportConstant.Greyhounds], ItemType.Sport, children, true);
    }

    setFixtureSelection(url: string, tree: TreeItem, dayOfRace: DayOfRaceDate, hasFixtureId: boolean): TreeItem {
        const today = this.urlConfig.translations[RacingUrlParam.Today];
        const tomorrow = this.urlConfig.translations[RacingUrlParam.Tomorrow];
        const NextRaces = this.urlConfig.translations[RacingUrlParam.NextRaces];

        const dayOfRaceByTranslation = this.getDayofRace(dayOfRace);
        const dayOfRaceByUrlParam = dayOfRace === DayOfRaceDate.Today ? today : tomorrow;

        const day = url.includes(tomorrow) ? tomorrow : today;
        const meetingId = this.getMeetingId(url);
        const fixtureId = this.getFixtureId(url);
        const matcher = url.match(/([^\/]*)\/*$/);
        const isNextRace = matcher ? matcher[1] === NextRaces : false;

        tree = this.disableClick(tree);

        if (isNextRace) {
            tree = this.expand(tree);
        } else if (!isNextRace && !meetingId) {
            tree = day === dayOfRaceByUrlParam && tree.title === dayOfRaceByTranslation ? this.expand(tree) : this.collapse(tree);
        } else if (meetingId && fixtureId === 0) {
            tree = day === dayOfRaceByUrlParam ? this.setTreeContextAndExpand(tree, dayOfRaceByTranslation, url) : this.collapse(tree);
        } else if (meetingId && fixtureId > 0) {
            tree = hasFixtureId ? this.setTreeContextAndExpand(tree, dayOfRaceByTranslation, url) : this.collapse(tree);
        }

        return tree;
    }

    private setTreeContextAndExpand(tree: TreeItem, dayOfRaceByTranslation: string, url: string) {
        if (tree.title === dayOfRaceByTranslation) {
            tree = this.setTreeContextByRoute(url, tree);
            tree = this.expand(tree);
        }

        return tree;
    }

    private collapse(tree: TreeItem) {
        if (tree.expanded) tree = this.toggleExpanded(tree, tree);

        return tree;
    }

    getFixtureId(url: string): number {
        const regex = /\/(?!1:)\d+:(\d+)$/;
        const match = url.match(regex);

        return match ? Number(match[1]) : 0;
    }

    getMeetingId(url: string): number {
        const match = url.match(/\d{3,}/);

        return match ? Number(match.pop()) : 0;
    }

    private expand(tree: TreeItem) {
        if (!tree.expanded) tree = this.toggleExpanded(tree, tree);

        return tree;
    }

    private setTreeContextByRoute(url: string, tree: TreeItem): TreeItem {
        const meetingId = this.getMeetingId(url);

        return this.setByRouteParam(meetingId, tree);
    }
}
